import {
  BulbOutlined,
  CloudUploadOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Attachments, AttachmentsProps } from '@ant-design/x'
import { Collapse, Flex, message, Typography, UploadFile } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'

import {
  useUploadToAppSession,
  useUploadToOrganization,
} from '~/plugins/upload/client'
import { useFileTypes } from './hooks/useFileTypes'

export type InputUploadItem = {
  uid: string
  lastModified: number
  lastModifiedDate: Date
  size: number
  type: string
  percent: number
  status: string
  name: string
  urlPrivate: string
  file?: File
}

type Props = {
  id?: string
  value?: InputUploadItem[]
  initialValues?: InputUploadItem[]
  onChange?: (items: InputUploadItem[]) => void
  formValuesConfiguration?: any
  typesAllowed?: Record<string, boolean>
  isRequestSkipped?: boolean
  maxFileSizeMB?: number
  onUploadingChange?: (isUploading: boolean) => void
} & Omit<AttachmentsProps, 'onChange'>

export const InputUpload = ({
  id,
  value,
  initialValues,
  onChange,
  formValuesConfiguration = {},
  placeholder,
  isRequestSkipped = false,
  maxFileSizeMB = 25,
  typesAllowed = {},
  onUploadingChange,
  overflow = 'scrollX',
  ...props
}: Props) => {
  const { appSessionId, organizationId } = formValuesConfiguration

  const [items, setItems] = useState<AttachmentsProps['items']>([])
  const [countFilesUploaded, setCountFilesUploaded] = useState(0)

  useEffect(() => {
    setItems((value as AttachmentsProps['items']) ?? [])
  }, [value])

  const { mutateAsync: uploadToAppSession } = useUploadToAppSession()
  const { mutateAsync: uploadToOrganization } = useUploadToOrganization()

  const { typesAllowedEnsured, typeNames, getTypeFromExtensionFallback } =
    useFileTypes({ typesAllowed })

  const handleBeforeUpload = (file: UploadFile) => {
    const type = file.type || getTypeFromExtensionFallback(file.name)

    const isTypeAllowed = typesAllowedEnsured.includes(type)
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024

    if (!isTypeAllowed) {
      const name = typeNames[type] ?? type ?? 'unknown'
      message.error(`${name} is not supported`)
      return false
    }

    if (file.size > maxFileSizeBytes) {
      message.error(`File size exceeds the ${maxFileSizeMB}MB limit`)
      return false
    }

    return true
  }

  const handleUpload: AttachmentsProps['customRequest'] = async options => {
    if (!appSessionId && !organizationId && !isRequestSkipped) {
      throw new Error('appSession / organizationId is required to upload files')
    }

    const file = options.file as File

    if (isRequestSkipped) {
      options.onSuccess({}, file)
      return
    }

    onUploadingChange?.(true)

    try {
      if (appSessionId) {
        const response = await uploadToAppSession({
          file: file,
          appSessionId: appSessionId,
        })

        options.onSuccess(response, file)
      } else {
        const response = await uploadToOrganization({
          file: file,
          organizationId: organizationId,
        })

        options.onSuccess(response, file)
      }
    } catch (error) {
      options.onError(error)
      console.error('Unknown Error', error)
    }
  }

  const handleChange: AttachmentsProps['onChange'] = event => {
    const urlPrivate = event.file?.response?.url ?? event.file?.['urlPrivate']

    const fileList = event.fileList.filter(
      file => file.status === 'uploading' || file.status === 'done',
    )

    setItems(fileList)

    const fileListUploaded = fileList.filter(file => file.status === 'done')

    setCountFilesUploaded(fileListUploaded.length)

    const filesFormatted: InputUploadItem[] = fileList.map(file => ({
      uid: file.uid,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
      size: file.size,
      type: file.type,
      percent: file.percent,
      status: file.status,
      name: file.name,
      urlPrivate: file.response?.url ?? file['urlPrivate'],
      file: isRequestSkipped ? file.originFileObj : undefined,
    }))

    if (onChange && (urlPrivate || isRequestSkipped)) {
      onChange(filesFormatted)
    }
  }

  useEffect(() => {
    if (countFilesUploaded === items.length) {
      onUploadingChange?.(false)
    }
  }, [countFilesUploaded])

  const acceptedFormats = useMemo(() => {
    return typesAllowedEnsured.map(dirname => typeNames[dirname] ?? dirname)
  }, [typesAllowedEnsured])

  const isMaxCount = Toolbox.Utility.isDefined(props.maxCount)

  return (
    <span id={id}>
      {!appSessionId && !organizationId && !isRequestSkipped && (
        <Flex justify="center" className="w-full">
          <Typography.Text type="warning">
            <WarningFilled /> Coult not identified your session
          </Typography.Text>
        </Flex>
      )}
      <Attachments
        multiple={props.multiple ?? true}
        accept={typesAllowedEnsured.join(',')}
        {...props}
        disabled={
          (!appSessionId && !organizationId && !isRequestSkipped) ||
          props['readOnly'] ||
          props['disabled']
        }
        beforeUpload={handleBeforeUpload}
        items={items}
        customRequest={handleUpload}
        onChange={handleChange}
        overflow={overflow}
        placeholder={_ => ({
          icon: <CloudUploadOutlined />,
          title: `Upload files${isMaxCount ? ` (${props.maxCount} max)` : ''}`,
          description: (
            <>
              Click or drag files to this area to upload
              <br />
              {Toolbox.Utility.isDefined(placeholder) ? (
                <>
                  {placeholder as string}
                  <br />
                </>
              ) : null}
              <Flex
                className="w-full"
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                <Collapse
                  className="w-full"
                  style={{ borderColor: 'transparent' }}
                  items={[
                    {
                      key: '1',
                      label: (
                        <>
                          <Typography.Text type="secondary">
                            <BulbOutlined /> Accepted formats
                          </Typography.Text>
                        </>
                      ),
                      children: (
                        <Flex vertical>
                          {acceptedFormats.map((format, index) => (
                            <Typography.Text
                              key={index}
                              type="secondary"
                              style={{ fontSize: '12px' }}
                            >
                              {format}
                            </Typography.Text>
                          ))}
                        </Flex>
                      ),
                    },
                  ]}
                />
              </Flex>
            </>
          ),
        })}
      />
    </span>
  )
}
